// Per-province contractor licence configuration.
//
// IMPORTANT — Registry URLs verified Feb 2026 (visually + HTTP 200):
//  QC → RBQ official registry              ✅ direct search
//  ON → HCRA Ontario Builder Directory     ✅ direct search
//  AB → Service Alberta Licensed Business  ✅ direct search
//  BC → BC Housing Licensed Builder Reg.   ✅ direct search (new domain confirmed by user)
//  SK → SATCC Check Credentials            ✅ direct search (deep-link confirmed by user)
//
//  MB → ⚠️ No central registry exists. Hide the "Open registry" button.
//  NB/NS/PE/NL/YT/NT/NU → ⚠️ Same — no provincial public registry. Hide the button.
//
// `hasRegistry: false` means the UI should NOT show the "Open registry" button
// for that province and instead display an honest disclaimer.

const PROVIDERS = {
  QC: {
    short: 'RBQ',
    full: 'Régie du bâtiment du Québec (RBQ)',
    placeholder: '0000-0000-00',
    registryUrl: 'https://www.pes.rbq.gouv.qc.ca/RegistreLicences/p1',
    registryName: 'RBQ',
    badgeStyle: 'rbq',
    hasRegistry: true,
  },
  ON: {
    short: 'HCRA',
    full: 'Home Construction Regulatory Authority (HCRA)',
    placeholder: 'B00000',
    registryUrl: 'https://obd.hcraontario.ca/',
    registryName: 'HCRA',
    badgeStyle: 'prefixed',
    hasRegistry: true,
  },
  AB: {
    short: 'Licence',
    full: 'Prepaid Contracting Business Licence — Service Alberta',
    placeholder: '000000',
    registryUrl: 'https://www.servicealberta.gov.ab.ca/consumer/business_search/',
    registryName: 'Service Alberta',
    badgeStyle: 'plain',
    hasRegistry: true,
  },
  BC: {
    short: 'BC Housing',
    full: 'Licensed Residential Builder — BC Housing',
    placeholder: '00000',
    registryUrl: 'https://licensedbuilderregistry.bchousing.org/LicenceRegistry/LicenceSearch',
    registryName: 'BC Housing',
    badgeStyle: 'prefixed',
    hasRegistry: true,
  },
  SK: {
    short: 'SATCC',
    full: 'Saskatchewan Apprenticeship and Trade Certification Commission',
    placeholder: '000000',
    registryUrl: 'https://saskapprenticeship.ca/check-credentials/',
    registryName: 'SATCC',
    badgeStyle: 'prefixed',
    hasRegistry: true,
  },
  MB: {
    short: 'Licence',
    full: 'Licence d\u2019entrepreneur — Manitoba',
    placeholder: '',
    registryUrl: null,
    registryName: 'Manitoba',
    badgeStyle: 'plain',
    hasRegistry: false,
    // Honest disclaimer shown in place of the verify button.
    noRegistryNoticeFr:
      "Le Manitoba n'a pas de registre public centralisé des entrepreneurs en construction. " +
      "Vérifiez auprès de votre municipalité, du Manitoba Workers Compensation Board, " +
      "ou de l'organisme du trade concerné.",
    noRegistryNoticeEn:
      'Manitoba does not have a centralized public registry of construction contractors. ' +
      'Check with your municipality, the Manitoba Workers Compensation Board, ' +
      'or the relevant trade authority.',
  },
};

// Generic fallback for NB, NS, PE, NL, YT, NT, NU — same story as MB.
const FALLBACK = {
  short: 'Licence',
  full: 'Licence d\u2019entrepreneur',
  placeholder: '',
  registryUrl: null,
  registryName: '',
  badgeStyle: 'plain',
  hasRegistry: false,
  noRegistryNoticeFr:
    "Cette province n'a pas de registre public centralisé des entrepreneurs en construction. " +
    "Vérifiez auprès de votre municipalité, de la commission provinciale des accidents du travail (WCB), " +
    "ou de l'organisme du trade concerné.",
  noRegistryNoticeEn:
    'This province does not have a centralized public registry of construction contractors. ' +
    'Check with your municipality, the provincial Workers Compensation Board (WCB), ' +
    'or the relevant trade authority.',
};

/**
 * Returns the licence-provider config for a given province code.
 */
export function getLicenceProvider(province) {
  const code = (province || 'QC').toUpperCase();
  return PROVIDERS[code] || FALLBACK;
}

/**
 * Field label shown above the licence number input.
 */
export function licenceFieldLabel(province, lang = 'fr') {
  const p = getLicenceProvider(province);
  if (p.short === 'Licence') {
    return lang === 'fr' ? 'Numéro de licence d\u2019entrepreneur' : 'Contractor licence number';
  }
  return lang === 'fr'
    ? `Num\u00e9ro de licence ${p.short}`
    : `${p.short} licence number`;
}

/**
 * Compact badge text used on the PDF header and the SignPage banner.
 */
export function licenceBadgeText(province, number, lang = 'fr') {
  const p = getLicenceProvider(province);
  const num = String(number || '').trim();

  if (lang === 'en') {
    if (p.badgeStyle === 'rbq')      return `${p.short.toUpperCase()} VERIFIED \u00b7 ${num}`;
    if (p.badgeStyle === 'prefixed') return `LICENCE VERIFIED \u00b7 ${p.short.toUpperCase()} ${num}`;
    return `LICENCE VERIFIED \u00b7 ${num}`;
  }
  if (p.badgeStyle === 'rbq')      return `${p.short.toUpperCase()} V\u00c9RIFI\u00c9 \u00b7 ${num}`;
  if (p.badgeStyle === 'prefixed') return `LICENCE V\u00c9RIFI\u00c9E \u00b7 ${p.short.toUpperCase()} ${num}`;
  return `LICENCE V\u00c9RIFI\u00c9E \u00b7 ${num}`;
}

/**
 * Card title used in the Profile verification block.
 */
export function licenceCardTitle(province, lang = 'fr') {
  const p = getLicenceProvider(province);
  if (p.short === 'Licence') {
    return lang === 'fr' ? 'Vérification de licence' : 'Licence verification';
  }
  return lang === 'fr'
    ? `V\u00e9rification de licence ${p.short}`
    : `${p.short} licence verification`;
}

/**
 * Label of the "Open registry" button (only when hasRegistry is true).
 */
export function registryButtonLabel(province, lang = 'fr') {
  const p = getLicenceProvider(province);
  return lang === 'fr'
    ? `Ouvrir le registre ${p.registryName}`
    : `Open ${p.registryName} registry`;
}

/**
 * Honest disclaimer shown when the province has no central public registry.
 */
export function noRegistryNotice(province, lang = 'fr') {
  const p = getLicenceProvider(province);
  return lang === 'fr' ? (p.noRegistryNoticeFr || '') : (p.noRegistryNoticeEn || '');
}
